import { showGenericErrorToast } from 'services/toaster';
import { logInfo } from 'setup/logger/logInfo';
import { channelServer } from 'shared/channels';
import { tokenContract } from 'shared/domain/auth/tokenContract';
import type { TokenErrorReason } from 'shared/domain/auth/tokenErrorReason';
import type { TokenAnswer, TokenPayload } from 'shared/domain/auth/tokenModel';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { getErrorMessage } from 'shared/utils/errorHandling';
import { getFreshToken, TokenBrokerError } from './tokenBroker';

// Below the worker's token timeout (30 s), so slow Auth0 responses show up before it gives up.
const SLOW_TOKEN_RESPONSE_MS = 5000;
// The worker waits 30 s; answering earlier gives it a real refusal instead of a timeout.
export const TOKEN_RESPONSE_DEADLINE_MS = 20_000;

let isServing = false;

function handleTokenErrorUx(reason: TokenErrorReason): void {
  if (
    reason === 'unauthenticated' ||
    reason === 'auth0_unavailable' ||
    reason === 'degraded' ||
    // The refresh keeps running and may still succeed.
    reason === 'timeout'
  ) {
    return;
  }
  showGenericErrorToast();
}

// Does not cancel the refresh: later requests reuse the in-flight one or the cached result.
function withDeadline(refresh: Promise<TokenPayload>): Promise<TokenPayload> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new TokenBrokerError('timeout', `Token refresh exceeded ${TOKEN_RESPONSE_DEADLINE_MS}ms`));
    }, TOKEN_RESPONSE_DEADLINE_MS);
  });
  return Promise.race([refresh, deadline]).finally(() => clearTimeout(timeoutId));
}

/** Sends a token the worker did not ask for (it lifts its auth cooldown). */
function publishToken(token: TokenPayload): void {
  channelServer.publish(tokenContract, DomainMessagesTypes.tokenPush, token);
}

/** After a timeout the refresh keeps running: its token lifts the worker's cooldown later. */
function publishWhenRefreshed(refresh: Promise<TokenPayload>, startedAt: number): void {
  refresh.then(
    (token) => {
      // Skip once the page stopped serving (logout, provider unmounted).
      if (isServing) {
        publishToken(token);
      }
    },
    (lateError: unknown) =>
      logInfo('sw-token-request-failed', {
        reason: 'late_failure',
        durationMs: Date.now() - startedAt,
        error: getErrorMessage(lateError),
      })
  );
}

/** The page's answer to a token request of the worker, within the deadline. */
export async function answerTokenRequest(): Promise<TokenAnswer> {
  const startedAt = Date.now();
  const refresh = getFreshToken();
  try {
    const token = await withDeadline(refresh);
    const durationMs = Date.now() - startedAt;
    if (durationMs >= SLOW_TOKEN_RESPONSE_MS) {
      logInfo('sw-token-request-slow', { durationMs });
    }
    return { status: 'granted', ...token };
  } catch (error) {
    const reason = error instanceof TokenBrokerError ? error.reason : 'refresh_failed';
    // degraded/unauthenticated are instant, expected refusals; logging them would only add noise.
    if (reason !== 'degraded' && reason !== 'unauthenticated') {
      logInfo('sw-token-request-failed', {
        reason,
        durationMs: Date.now() - startedAt,
        error: getErrorMessage(error),
      });
    }
    if (reason === 'timeout') {
      publishWhenRefreshed(refresh, startedAt);
    }
    handleTokenErrorUx(reason);
    return { status: 'refused', reason };
  }
}

/**
 * Answers the worker's token requests. Wire in Auth0Provider after
 * configureTokenBroker(); returns the stop.
 */
export function serveTokenRequests(): () => void {
  const stopServing = channelServer.handle(tokenContract, { getToken: answerTokenRequest });
  isServing = true;
  return (): void => {
    isServing = false;
    stopServing();
  };
}

/** Sends a fresh token to the worker (tab resumed, session restored); best effort. */
export async function pushTokenOnResume(): Promise<void> {
  if (!isServing) {
    return;
  }
  try {
    publishToken(await getFreshToken({ force: false }));
  } catch {
    // Best-effort push; the worker asks when it needs a token.
  }
}
